import { Canvas } from "./Canvas";
import { Focus } from "./Focus";
import { FieldFormatter } from "./FieldFormatter";
import { FieldError, RecordErrorSet } from "./RecordErrorSet";
import { IFieldMetaProps, IFieldOption, RecordMeta } from "./RecordMeta";
import { IFieldDefinition, IFieldLookup, Schema } from "./Schema";
import { FieldType, IEventScope, IFilterValue } from "./types";
import { ActionRef } from "./ActionRef";

export class FieldRef {
    public name: string;
    public record:any;
    public meta: RecordMeta;
    public canvas:Canvas;
    public scope:IEventScope;
    public def:IFieldDefinition;
    public lookupLabelRef:FieldRef;
    public recordName:string;
    public isExpression:boolean;
    public exprValue:any;
    public formatOverride:string;
    public labelOverride:string;

    constructor(canvas:Canvas,scope:IEventScope,data:any,name:string,recordName:string){
        this.canvas = canvas;
        this.scope = scope;
        this.recordName = recordName;
        if (data){
            this.record = data;
            this.meta = data.$$meta;
            if (this.meta){
                let def = Schema.getFieldFromMeta(this.meta,name);
                if (def){
                    this.def = def;
                    name = def.name;
                    if (def.lookupLabel){
                        this.lookupLabelRef = canvas.getFieldRef(def.lookupLabel,scope);
                    }
                }
            }
        }
        this.name = name;
    }
    
    get fieldMeta():IFieldMetaProps {
        if (!this.meta || !this.meta.metaProps) return null;
        var meta = this.meta.metaProps;
        if (!meta) return null;
        return meta[this.name];
    }

    get value(): any {
        if (this.isExpression) return this.exprValue;
        if (!this.record) return null;
        let v;
        if (this.name[0] == '@'){
            v = this.getRecordValue(this.name.substr(1));
        }
        else {
            v = this.getRecordValue(this.name);
        }
        if (this.def && this.def.isRange){
            if (v){
                return v.value;
            }
            return null;
        }
        return v;
    }

    get filter(): IFilterValue {
        let v:any;
        if (this.name[0] == '@'){
            v = this.getRecordValue(this.name.substring(1));
        }
        else {
            v = this.getRecordValue(this.name);
        }
        return v 
    }

    private getRecordValue(name:string):any {
        if (this.record.$$get){
            return this.record.$$get(name);
        }
        return this.record[name];
    }

    async setValue(value:any){
        if (this.def && this.def.isRange){
            let filter = this.filter || {$type:"filter"};
            filter.value = value;
            await this.canvas.setValue(this,filter);
        }
        else {
            await this.canvas.setValue(this,value);
        }
    }

    async setValueNoUpdate(value:any){
        if (this.def && this.def.isRange){
            let filter = this.filter || {$type:"filter"};
            if (value && typeof(value) === "string"){
                let i = value.indexOf('|');
                if (i != -1){
                    filter.value = value.substring(0,i);
                    filter.toValue = value.substring(i + 1);
                }
                else {
                    filter.value = value;
                }
            }
            else {
                filter.value = value;
            }
            await this.canvas.setRecordValueNoUpdate(this,filter);
        }
        else {
            await this.canvas.setRecordValueNoUpdate(this,value);
        }
    }

    get shouldFocus():boolean {
        if (this.readonly || this.disabled) return false;
        return Focus.shouldFocus(this.canvas,this);
    }


    get disabled(): boolean {
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            let value = fieldMeta.disabled;
            if (value !== undefined) return value;
        }
    }

    get disabledHelp(): string {
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            return fieldMeta.disabledHelp;
        }
    }

    get styles():string {
        if (this.def && this.def.styles){
            return this.canvas.getValue(this.def.styles,{scope:this.scope});
        }
    }

    get labelStyles():string {
        if (this.def && this.def.labelStyles){
            return this.canvas.getValue(this.def.labelStyles,{scope:this.scope});
        }
    }

    get suffix():string {
        if (this.def && this.def.suffixExpression){
            return this.canvas.getValue(this.def.suffixExpression,{scope:this.scope});
        }
    }
    
    get size():number {
        if (this.def){
            return this.def.size;
        }
    }
    
    get readonly():boolean {
        if (this.meta && this.meta.readonly) return true;
        if (this.def && this.def.readonly) return true;
        if (this.isExpression) return true;
        return false;
    }
    
    get hidden(): boolean {
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            let value = fieldMeta.hidden;
            if (value !== undefined) return value;
        }
        if (this.def){
            return this.def.hidden;
        }
    }

    get required(): boolean {
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            let value = fieldMeta.required;
            if (value !== undefined) return value;
        }
        if (this.def){
            return this.def.required;
        }
    }

    get options(): IFieldOption[] {
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            let value = fieldMeta.options;
            if (value !== undefined) return value;
        }
        if (this.def){
            return this.def.options;
        }
    }

    valueIsOption(option:IFieldOption):boolean {
        if (!option) return false;
        let value = this.value;
        let upValue;
        if (value && value.toUpperCase){
            upValue = value.toUpperCase();
        }
        return upValue == option.value;
    }

    get formattedValue(): string {
        return this.formatValue(this.value);
    }

    private formatValue(value:any):string {
        var options = this.options;
    
        if (options && this.type != "date") {
            return this.getOptionLabel(options,value);
        }
        else {
            var format = this.format;
            let formatted = FieldFormatter.format(value, this.type, format);
            return formatted;
        }
        return value;
    }

    get formattedToValue():string {
        return this.formatValue(this.toValue);
    }

    private getOptionLabel(options:IFieldOption[],value:any):string {
        if (!options) return value;
        let compare:string;
        if (value === null || value === undefined){
            compare = "";
        }
        else if (value.toUpperCase){
            compare = value.toUpperCase();
        }
        else {
            compare = value.toString();
        }
        if (compare){
            for(let i =0 ; i < options.length;i++){
                let opt = options[i];
                if (opt.value){
                    if (opt.value.toUpperCase){
                        if (opt.value.toUpperCase() == compare) return opt.label;
                    }
                    else {
                        if (opt.value == compare) return opt.label;
                    }
                }
            }
            return value;
        }
        // looking for option without value;
        for (var i = 0; i < options.length; i++) {
            let opt = options[i];
            if (!opt.value) return opt.label;
        }
        return value;
    }

    get error(): FieldError {
        var error = RecordErrorSet.get(this.meta);
        if (!error || !error.fields) return null;
        
        return error.fields[this.name];
    }

    clearError() {
        var error = RecordErrorSet.get(this.meta);
        if (error){
            error.fields[this.name] = null;
        }
    }

    handleEnterKey(){
        this.canvas.handleEnterKey(this.scope);
    }

    get label():string {
        if (this.labelOverride) return this.labelOverride;
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            let metaValue = fieldMeta.label;
            if (metaValue !== undefined) return metaValue;
        }
        if (this.def){
            return this.def.label;
        }
    }

    get placeholder():string {
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            let metaValue = fieldMeta.placeholder;
            if (metaValue !== undefined) return metaValue;
        }
        if (this.def){
            return this.def.placeholder;
        }
    }

    get valueRange():string {
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            let metaValue = fieldMeta.valueRange;
            if (metaValue !== undefined) return metaValue;
        }
        if (this.def){
            return this.def.valueRange;
        }
    }

    get format():string {
        if (this.formatOverride) return this.formatOverride;
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            let metaValue = fieldMeta.format;
            if (metaValue !== undefined) return metaValue;
        }
        if (this.def){
            return this.def.format;
        }
    }

    get help():string {
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            let metaValue = fieldMeta.help;
            if (metaValue !== undefined) return metaValue;
        }
        if (this.def){
            return this.def.help;
        }
    }

    get lookup():IFieldLookup {
        if (this.def) {
            return this.def.lookup;
        }
    }

    get type():FieldType {
        if (this.def){
            return this.def.type;
        }
    }

   

    get lookupLabel():string {
        if (this.lookupLabelRef){
            return this.lookupLabelRef.formattedValue;
        }
        let fieldMeta = this.fieldMeta;
        if (fieldMeta){
            return fieldMeta.lookupLabel || "";
        }
        return "";
    }

    set lookupLabel(value:string){
        if (this.lookupLabelRef){
            this.canvas.setRecordValueNoUpdate(this.lookupLabelRef,value);
        }
        else if (this.meta){
           RecordMeta.setLookupLabel(this.meta,this.name,value);
        }
    }

    get onClickAction():ActionRef {
        if (!this.def || !this.def.onClick) return null;
        let meta =  RecordMeta.getFieldMeta(this.canvas.$$meta,this.def.onClick);
        if (meta && meta.hidden){
            return null;
        }
        return new ActionRef(this.canvas,this.def.onClick,this.scope);
    }

    get onValueChanged():string {
        if (this.def && this.def.onValueChanged) return this.def.onValueChanged;
        let recordMeta = RecordMeta.forRecord(this.record);
        if (recordMeta && recordMeta.schema.onValueChanged){
            return recordMeta.schema.onValueChanged;
        }
    }
    async fireOnClick(){
        let actionRef = new ActionRef(this.canvas,this.def.onClick,this.scope);
        await actionRef.trigger();
    }

    async setCompare(compare:string,triggerOnChange:boolean){
        let filter = this.filter || {$type:"filter"};
        filter.compare = compare;
        if (triggerOnChange){
            await this.canvas.setValue(this,filter);
        }
        else {
            this.canvas.setRecordValueNoUpdate(this,filter);
        }
    }

    get compare():string {
        let filter = this.filter;
        if (filter) return filter.compare;
        return null;
    }

    async setBucket(compare:string){
        let filter = this.filter || {$type:"filter"};
        filter.bucket = compare;
        await this.canvas.setValue(this,filter);
    }

    get bucket():string {
        let filter = this.filter;
        if (filter) return filter.bucket;
        return null;
    }

    async setToValue(value:any){
        let filter = this.filter || {$type:'filter'};
        filter.toValue = value;
        await this.canvas.setValue(this,filter);
    }

    get toValue():any {
        let filter = this.filter;
        if (filter) return filter.toValue;
        return null;
    }

    get qualifiedName():string {
        if (this.recordName){
            return this.recordName + "." + this.name;
        }
        return this.name;
    }
   

}